import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { MessageCircle, Star, MapPin } from "lucide-react";
import { waLink } from "@/lib/api";
import { useContent } from "@/lib/useContent";

export const Hero = () => {
  const slides = useContent("hero_slides");
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);
  const slide = slides[idx % slides.length];

  return (
    <section className="relative min-h-[92vh] flex items-center overflow-hidden bg-[#051A2E]" data-testid="home-hero">
      <AnimatePresence>
        <motion.div key={idx} className="absolute inset-0"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 1.2 }}>
          <img src={slide.url} alt={`${slide.title} — luxury travel destination`} className="w-full h-full object-cover mk-kenburns" loading={idx === 0 ? "eager" : "lazy"} />
        </motion.div>
      </AnimatePresence>
      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-black/30" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-36 pb-40 w-full">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
          <p className="text-[#E3BA45] text-xs sm:text-sm uppercase tracking-[0.35em] mb-5 font-semibold">International Travel Company</p>
          <h1 className="font-serif-display text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.1] tracking-tight max-w-4xl">
            Explore the World <span className="text-[#E3BA45] italic">with Confidence</span>
          </h1>
          <p className="text-white/85 mt-6 text-sm sm:text-base leading-relaxed max-w-2xl" data-testid="hero-positioning-statement">
            Makchester Tours &amp; Travel is an international travel company registered in the United Arab Emirates, Zimbabwe and South Africa, serving travellers worldwide with flights, visas, hotels, holiday packages, tours and travel solutions.
          </p>
          <p className="text-white/60 mt-3 text-sm tracking-wide">🇦🇪 United Arab Emirates • 🇿🇼 Zimbabwe • 🇿🇦 South Africa</p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/contact" className="mk-btn-gold" data-testid="hero-quote-btn">Get a Free Quote</Link>
            <Link to="/holidays" className="mk-btn-navy !bg-white !text-[#0A2E4D] hover:!bg-slate-100" data-testid="hero-book-btn">Explore Holiday Packages</Link>
            <a href={waLink("Hello Makchester Tours & Travel, I would like to speak to a travel expert.")} target="_blank" rel="noreferrer"
               className="inline-flex items-center justify-center gap-2 rounded-full px-7 py-3.5 text-sm font-semibold text-white bg-[#25D366] hover:bg-[#1ebe57] transition-colors" data-testid="hero-whatsapp-btn">
              <MessageCircle size={16} /> WhatsApp a Travel Expert
            </a>
          </div>

          <div className="mt-8 flex items-center gap-3">
            <div className="flex gap-0.5">{[0,1,2,3,4].map((i) => <Star key={i} size={16} className="text-[#E3BA45] fill-[#E3BA45]" />)}</div>
            <span className="text-white/80 text-sm font-medium tracking-wide">Trusted by Travellers Worldwide</span>
          </div>
        </motion.div>

        <div className="absolute bottom-8 right-6 lg:right-10 hidden md:flex items-center gap-2 text-white/80 text-sm">
          <MapPin size={14} className="text-[#E3BA45]" /> <span className="font-serif-display text-lg text-white">{slide.title}</span> — {slide.sub}
        </div>
        <div className="absolute bottom-8 left-6 lg:left-10 hidden md:flex gap-2">
          {slides.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
              className={`h-1.5 rounded-full transition-all duration-500 ${i === idx ? "w-8 bg-[#E3BA45]" : "w-3 bg-white/40"}`} />
          ))}
        </div>
      </div>
    </section>
  );
};
